import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { Validators } from '@angular/forms';

export enum CARRIER_CODE {
  VTP = "VTP",
  GHN = "GHN",
}

export enum CARRIER_ACCOUNT_HEALTH_STATUS {
  CONNECTED = 'CONNECTED',
  SUSPENDED = 'SUSPENDED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  UNREACHABLE = 'UNREACHABLE',
  UNKNOWN = 'UNKNOWN',
}

export const CARRIER_OPTIONS = [
  { label: 'Viettel Post', value: 'VTP' },
  { label: 'Giao Hàng Nhanh', value: 'GHN', disabled: true },
  { label: 'Vietnam Post', value: 'VNP', disabled: true },
  { label: 'Giao Hàng Tiết Kiệm', value: 'GHTK', disabled: true },
  { label: 'J&T Express', value: 'JTX', disabled: true },
];

export const CARRIER_STATUS_FILTER_OPTIONS = [
  { label: 'carrier.status.active', value: 'ACTIVE' },
  { label: 'carrier.status.inactive', value: 'INACTIVE' },
];

export const CARRIER_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'providerName',
    label: 'carrier.search.fields.provider',
    type: 'text',
    placeholder: 'carrier.search.placeholders.enter_provider_name',
  },
  {
    key: 'accountCode',
    label: 'carrier.search.fields.account_code',
    type: 'text',
    placeholder: 'carrier.search.placeholders.enter_account_code',
  },
  {
    key: 'status',
    label: 'carrier.search.fields.status',
    type: 'select',
    options: CARRIER_STATUS_FILTER_OPTIONS,
    placeholder: 'carrier.search.placeholders.select_status',
    showClear: true,
  },
];

export const CARRIER_COLUMNS: TableColumn[] = [
  {
    label: 'carrier.table.columns.provider_code',
    prop: 'providerName',
    width: 150,
    align: 'left',
  },
  {
    label: 'carrier.table.columns.account_code',
    prop: 'code',
    width: 150,
    align: 'left',
  },
  {
    label: 'carrier.table.columns.note',
    prop: 'description',
    width: 200,
    align: 'left',
  },
  {
    label: 'carrier.table.columns.status',
    prop: 'status',
    width: 120,
    align: 'center',
    type: 'custom',
  },
  {
    label: 'carrier.table.columns.health',
    prop: 'health',
    width: 120,
    align: 'center',
    type: 'custom',
  },
  {
    label: 'carrier.table.columns.operations',
    prop: 'actions',
    width: 300,
    align: 'center',
    type: 'actions',
  },
];

// ============================================================================
// STATUS HELPERS
// ============================================================================

export function getStatusSeverity(
  status: string
): 'success' | 'warning' | 'danger' | 'secondary' {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'secondary';
    default:
      return 'secondary';
  }
}

export function getStatusDisplayText(
  status: string,
  translateService: any
): string {
  const statusKey = `carrier.status.${status?.toLowerCase()}`;
  return translateService.instant(statusKey) || status;
}

export function getHealthStatusDisplayText(
  health: CARRIER_ACCOUNT_HEALTH_STATUS,
  translateService: any
): string {
  const statusKey = `carrier.health.${health?.toLowerCase()}`;
  return translateService.instant(statusKey) || health;
}

// ============================================================================
// FORM CONFIGURATION
// ============================================================================

export interface CarrierFormConfig {
  title: string;
  formFields: FormField[];
  headerActions: ActionButton[];
  listRoute: string;
}

export const CARRIER_FORM_CONFIG: CarrierFormConfig = {
  title: 'carrier.form.title_add',
  formFields: [
    {
      label: 'carrier.form.fields.provider',
      controlName: 'providerCode',
      type: 'dropdown',
      placeholder: 'carrier.form.placeholders.select_provider',
      required: true,
      section: 'carrier.form.sections.basic_info',
      options: CARRIER_OPTIONS,
      validators: [Validators.required],
      errorMessages: {
        required: 'carrier.messages.error.provider_required',
      },
    },
    {
      label: 'carrier.form.fields.description',
      controlName: 'description',
      type: 'text',
      placeholder: 'carrier.form.placeholders.enter_description',
      required: false,
      section: 'carrier.form.sections.basic_info',
      validators: [],
      errorMessages: {},
    },
    {
      label: 'carrier.form.fields.status',
      controlName: 'status',
      type: 'checkbox',
      section: 'carrier.form.sections.basic_info',
      defaultValue: true,
      validators: [],
      errorMessages: {},
    },
    {
      label: 'carrier.form.fields.username',
      controlName: 'vtp_username',
      type: 'text',
      placeholder: 'carrier.form.placeholders.enter_username',
      required: true,
      section: 'carrier.form.sections.connection_info',
      dependsOn: 'providerCode',
      validators: [Validators.required],
      errorMessages: {
        required: 'carrier.messages.error.username_required',
      },
    },
    {
      label: 'carrier.form.fields.password',
      controlName: 'vtp_password',
      type: 'password',
      placeholder: 'carrier.form.placeholders.enter_password',
      required: true,
      section: 'carrier.form.sections.connection_info',
      dependsOn: 'providerCode',
      validators: [Validators.required],
      errorMessages: {
        required: 'carrier.messages.error.password_required',
      },
    },
    {
      label: 'carrier.form.fields.webhook_secret',
      controlName: 'webhookSecret',
      type: 'password',
      placeholder: 'carrier.form.placeholders.webhook_secret',
      required: false,
      section: 'carrier.form.sections.connection_info',
      dependsOn: 'providerCode',
      readonly: true,
      passwordToggleWhenReadonly: true,
      passwordReadonlyOnly: true,
      showCopyButton: true,
      validators: [],
      errorMessages: {},
      defaultValue: '',
    },
  ],
  headerActions: [
    {
      label: 'common.actions.cancel',
      action: 'cancel',
      severity: 'secondary',
      outlined: true,
    },
    {
      label: 'common.actions.save',
      action: 'save',
      severity: 'info',
    },
  ],
  listRoute: '/configuration/carrier',
};

